import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { API, fmtDate, formatPhone } from '@/lib/helpers';
import { formatBrazilDateTime } from '@/lib/timezone';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tooltip as UiTooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import TipTapEditor from '@/components/admin/TipTapEditor';
import { ArrowLeft, ArrowRight, BookOpenText, CalendarDays, CheckCircle2, ClipboardCheck, ClipboardList, Download, Eye, FileText, Info, Loader2, Mail, MessageSquare, Plus, Printer, Save, Smartphone, Sparkles, Trash2, UploadCloud, UserRound, UsersRound, X } from 'lucide-react';

const STATUS = {
  recebida: { label: 'Recebida', className: 'bg-orange-50 text-orange-700 border-orange-100' },
  corrigida: { label: 'Corrigida', className: 'bg-yellow-50 text-yellow-700 border-yellow-100' },
  aprovada: { label: 'Aprovada', className: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
  reprovada: { label: 'Reprovada', className: 'bg-red-50 text-red-700 border-red-100' },
};
const STORY_STATUS_FLOW = ['recebida', 'corrigida', 'aprovada'];
const ATTACHMENT_ROLE_LABEL = { colaboradora: 'Colaboradora', coordenadora: 'Coordenadora', ministerio: 'Ministério' };

function statusClass(status) {
  if (status === 'APROVADO') return 'bg-emerald-50 text-emerald-700 border-emerald-100';
  if (status === 'AJUSTAR') return 'bg-amber-50 text-amber-700 border-amber-100';
  if (status === 'REPROVADO') return 'bg-amber-50 text-amber-700 border-amber-100';
  return 'bg-amber-50 text-amber-700 border-amber-100';
}

function aiVerdictLabel(status) {
  return {
    APROVADO: 'Aprovado',
    AJUSTAR: 'Ajustar',
    'APROVADO COM AJUSTE': 'Ajustar',
    REPROVADO: 'Ajustar',
  }[status] || status;
}

const AI_RULE_LABELS = {
  1: 'Título da história', 2: 'Referência bíblica', 3: 'Fidelidade bíblica ACF', 4: 'Linguagem infantil', 5: 'Sentido bíblico preservado', 6: 'Faixa etária', 7: 'Temas sensíveis', 8: 'Fundamentos espirituais', 9: 'Linguagem neutra', 10: 'Termos pesados', 11: 'Sem dramatizações', 12: 'Tom reverente', 13: "Expressão 'Deus seja louvado'", 14: 'Fundamento', 15: 'Sem representações indevidas', 16: 'Atividade sugerida', 17: 'Objetivo'
};

const CORRECTION_FIELD_META = {
  title: { label: 'Título', testId: 'title', html: false },
  biblical_reference: { label: 'Referência', testId: 'biblical-reference', html: false },
  foundation: { label: 'Fundamento', testId: 'foundation', html: true },
  objective: { label: 'Objetivo', testId: 'objective', html: true },
};

function escapeHtml(value = '') {
  return String(value).replace(/[&<>"]/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[char]));
}

function suggestionToHtml(value = '') {
  const lines = String(value || '').split(/\n+/).map(line => line.trim()).filter(Boolean);
  return lines.length ? lines.map(line => `<p>${escapeHtml(line)}</p>`).join('') : '';
}

function stripHtml(value = '') {
  const element = document.createElement('div');
  element.innerHTML = String(value || '');
  return (element.textContent || element.innerText || '').trim();
}

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

async function pollEbiAiJob(storyId, jobId) {
  for (let attempt = 0; attempt < 90; attempt += 1) {
    await wait(2000);
    const r = await fetch(`${API}/ebi/stories/${storyId}/analyze/${jobId}`, { credentials: 'include' });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.detail || 'Erro ao acompanhar análise IA');
    if (data.status === 'completed' && data.analysis) return data.analysis;
    if (data.status === 'failed') throw new Error(data.error || 'Erro ao analisar história');
  }
  throw new Error('A análise ainda está em processamento. Tente abrir novamente em instantes.');
}

function StatusBadge({ status, testId = 'ebi-story-detail-status-badge' }) {
  const meta = STATUS[status] || STATUS.recebida;
  return <Badge className={meta.className} data-testid={testId}>{meta.label}</Badge>;
}

function AiRuleItem({ item }) {
  const title = AI_RULE_LABELS[item.item] || (item.regra || '').slice(0, 56);
  const showAdjustment = item.ajuste_necessario && item.ajuste_necessario.trim().toLowerCase() !== 'nenhum';
  const needsAttention = item.parecer !== 'APROVADO';
  return (
    <div className={`rounded-lg border p-3 ${needsAttention ? 'border-amber-200 bg-amber-50/50' : 'border-slate-200 bg-white'}`} data-testid={`ebi-story-detail-ai-rule-${item.item}`}>
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${statusClass(item.parecer)}`}>{item.parecer === 'APROVADO' ? <CheckCircle2 className="w-4 h-4" /> : <Info className="w-4 h-4" />}</div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <p className="text-sm font-semibold text-slate-900">{item.item}. {title}</p>
              <TooltipProvider><UiTooltip><TooltipTrigger asChild><button type="button" className="text-slate-400 hover:text-slate-700" data-testid={`ebi-story-detail-ai-rule-info-${item.item}`}><Info className="w-3.5 h-3.5" /></button></TooltipTrigger><TooltipContent className="max-w-xs"><p className="text-xs">{item.regra}</p></TooltipContent></UiTooltip></TooltipProvider>
            </div>
            <Badge className={statusClass(item.parecer)}>{item.parecer}</Badge>
          </div>
          {item.justificativa && <p className="mt-2 text-sm text-slate-700">{item.justificativa}</p>}
          {showAdjustment && <div className="mt-2 rounded-md bg-blue-50 border border-blue-100 px-3 py-2 text-sm text-blue-800"><span className="font-semibold">Ajuste sugerido: </span>{item.ajuste_necessario}</div>}
        </div>
      </div>
    </div>
  );
}

function AiAnalysisDialog({ analysis, open, onOpenChange }) {
  const result = analysis?.result || {};
  const items = Array.isArray(result.avaliacao_itens) ? result.avaliacao_itens : [];
  const approved = items.filter(item => item.parecer === 'APROVADO').length;
  const withAdjustments = items.filter(item => item.parecer !== 'APROVADO');
  const attentionItems = withAdjustments;
  const approvedItems = items.filter(item => item.parecer === 'APROVADO');
  const verdict = result.parecer_geral || 'AJUSTAR';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl h-auto max-h-[82vh] overflow-hidden p-0 sm:p-0" data-testid="ebi-story-detail-ai-modal">
        <div className="h-full min-h-0 flex flex-col bg-white">
          <div className="px-6 py-5 border-b border-slate-100 bg-white">
            <DialogHeader>
              <div className="flex items-stretch gap-3 pr-8">
                <div className="w-12 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center flex-shrink-0"><Sparkles className="w-6 h-6" /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2"><DialogTitle>Análise da história</DialogTitle><Badge className={statusClass(verdict)}>{verdict}</Badge></div>
                  <DialogDescription className="mt-1 flex flex-wrap items-center gap-1.5">
                    <span>{approved} de {items.length || 15} critérios aprovados{analysis?.created_at ? ` · ${formatBrazilDateTime(analysis.created_at, analysis.created_at)}` : ''}{analysis?.model ? ` · ${analysis.model}` : ''}</span>
                    {analysis?.cache_hit && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500" data-testid="ebi-story-detail-ai-cache-badge">reaproveitada</span>}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto p-6 bg-slate-50/70 space-y-5">
            <div className="grid gap-4 items-start">
              <div className="rounded-xl bg-white border border-slate-200 p-4" data-testid="ebi-story-detail-ai-summary-card">
                <div className="flex items-center gap-2 mb-2"><Info className="w-4 h-4 text-blue-600" /><h3 className="text-sm font-semibold text-slate-900">Resumo da IA</h3></div>
                <p className="text-sm text-slate-700" data-testid="ebi-story-detail-ai-summary">{result.resumo || 'Sem resumo gerado para esta análise.'}</p>
              </div>
            </div>

            <div className="rounded-xl bg-white border border-slate-200 p-4" data-testid="ebi-story-detail-ai-criteria-section">
              <div className="flex items-center gap-2 mb-3"><ClipboardCheck className="w-4 h-4 text-blue-600" /><h3 className="text-sm font-semibold text-slate-900">Critérios avaliados</h3></div>
              {items.length === 0 ? <p className="text-sm text-slate-500">Nenhum critério retornado nesta análise.</p> : <TooltipProvider><div className="flex flex-wrap gap-2" data-testid="ebi-story-detail-ai-criteria-chips">{[...withAdjustments, ...approvedItems].map(item => { const label = AI_RULE_LABELS[item.item] || (item.regra || '').slice(0, 56); const isApproved = item.parecer === 'APROVADO'; const chipClass = isApproved ? 'border-emerald-300 bg-emerald-50 text-emerald-900' : 'border-amber-300 bg-amber-50 text-amber-900'; return <UiTooltip key={item.item}><TooltipTrigger asChild><div className={`inline-flex max-w-full items-center gap-2 rounded-full border px-3 py-1.5 text-xs shadow-sm ${chipClass}`} data-testid={`ebi-story-detail-ai-criteria-chip-${item.item}`}><span className="truncate">{item.item}. {label}</span>{isApproved ? <CheckCircle2 className="w-4 h-4 flex-shrink-0 fill-emerald-600 text-white" /> : <Info className="w-4 h-4 flex-shrink-0 fill-amber-500 text-white" />}</div></TooltipTrigger><TooltipContent className="max-w-sm"><div className="space-y-1"><p className="font-semibold">{label}</p><p className="text-xs">Parecer: {aiVerdictLabel(item.parecer)}</p>{item.regra && <p className="text-xs text-slate-200">Critério: {item.regra}</p>}{item.justificativa && <p className="text-xs text-slate-200">Justificativa: {item.justificativa}</p>}{item.ajuste_necessario && item.ajuste_necessario.trim().toLowerCase() !== 'nenhum' && <p className="text-xs text-slate-200">Ajuste: {item.ajuste_necessario}</p>}{item.fundamento_sugerido && <p className="text-xs text-slate-200">Fundamento sugerido: {item.fundamento_sugerido}</p>}</div></TooltipContent></UiTooltip>; })}</div></TooltipProvider>}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function storyFileUrl(storyId, file) {
  if (storyId && file?.file_id) return `${API}/ebi/stories/${storyId}/files/${file.file_id}/download`;
  if (!file?.url) return '#';
  return file.url.startsWith('http') ? file.url : `${window.location.origin}${file.url}`;
}

function CorrectionButton({ field, suggestion, onApply }) {
  const meta = CORRECTION_FIELD_META[field];
  if (!suggestion || !meta) return null;
  return <Button type="button" variant="outline" size="sm" onClick={() => onApply(field)} className="h-6 px-2 text-[10px] bg-white border-blue-200 text-blue-700 hover:bg-blue-50" data-testid={`ebi-story-detail-apply-correction-${meta.testId}`}>Aplicar correção</Button>;
}

export function EbiStoryDetailContent({ storyId: storyIdProp, embedded = false, onBack, aiEnabled = null }) {
  const params = useParams();
  const storyId = storyIdProp || params.storyId;
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [story, setStory] = useState(null);
  const [form, setForm] = useState({});
  const [groups, setGroups] = useState([]);
  const [people, setPeople] = useState([]);
  const [aiSettings, setAiSettings] = useState({ enabled: true });
  const [aiAvailable, setAiAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoSaveState, setAutoSaveState] = useState('idle');
  const [analyzing, setAnalyzing] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [observationText, setObservationText] = useState('');
  const [observationPublic, setObservationPublic] = useState(false);
  const [observationDialogOpen, setObservationDialogOpen] = useState(false);
  const [deleteObservationTarget, setDeleteObservationTarget] = useState(null);
  const [deleteAttachmentTarget, setDeleteAttachmentTarget] = useState(null);
  const [downloadingFileId, setDownloadingFileId] = useState('');
  const [statusChangeTarget, setStatusChangeTarget] = useState(null);
  const [fieldCorrectionTarget, setFieldCorrectionTarget] = useState(null);
  const [ebiSettings, setEbiSettings] = useState(null);
  const initialLoadRef = useRef(true);
  const lastSavedSnapshotRef = useRef('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [meR, storyR, groupsR, peopleR, aiR, settingsR] = await Promise.all([
        fetch(`${API}/auth/me`, { credentials: 'include' }),
        fetch(`${API}/ebi/stories/${storyId}`, { credentials: 'include' }),
        fetch(`${API}/ebi/groups`, { credentials: 'include' }),
        fetch(`${API}/ebi/people`, { credentials: 'include' }),
        aiEnabled === false ? Promise.resolve(null) : fetch(`${API}/ebi/ai-settings`, { credentials: 'include' }).catch(() => null),
        fetch(`${API}/ebi/settings`, { credentials: 'include' }).catch(() => null),
      ]);
      if (!meR.ok) { if (!embedded) navigate('/'); return; }
      if (!storyR.ok) throw new Error('História não encontrada');
      const storyData = await storyR.json();
      setUser(await meR.json());
      setStory(storyData);
      setForm(storyData);
      lastSavedSnapshotRef.current = JSON.stringify(storyData);
      initialLoadRef.current = false;
      if (groupsR.ok) setGroups(await groupsR.json());
      if (peopleR.ok) setPeople(await peopleR.json());
      if (settingsR?.ok) setEbiSettings(await settingsR.json());
      if (aiEnabled === false) {
        setAiAvailable(false);
        setAiSettings({ enabled: false });
      } else if (aiR?.ok) {
        const aiData = await aiR.json();
        setAiSettings(aiData);
        setAiAvailable(aiData.enabled !== false);
      } else {
        setAiAvailable(false);
        setAiSettings({ enabled: false });
      }
    } catch (err) {
      toast.error(err.message || 'Erro ao carregar história');
    } finally {
      setLoading(false);
    }
  }, [aiEnabled, embedded, navigate, storyId]);

  useEffect(() => { loadData(); }, [loadData]);

  const storytellersForGroup = useMemo(() => {
    return people.filter(p => !p.deleted && p.active && (p.group_ids || []).includes(form.group_id) && ['colaboradora', 'coordenadora'].includes(p.role));
  }, [form.group_id, people]);

  const lastAnalysis = form.last_ai_analysis;
  const aiItems = Array.isArray(lastAnalysis?.result?.avaliacao_itens) ? lastAnalysis.result.avaliacao_itens : [];
  const aiApproved = aiItems.filter(item => item.parecer === 'APROVADO').length;
  const aiCorrections = lastAnalysis?.result?.correcoes_sugeridas || {};
  const currentStatusIndex = STORY_STATUS_FLOW.indexOf(form.status || 'recebida');
  const previousStatus = currentStatusIndex > 0 ? STORY_STATUS_FLOW[currentStatusIndex - 1] : null;
  const nextStatus = currentStatusIndex >= 0 && currentStatusIndex < STORY_STATUS_FLOW.length - 1 ? STORY_STATUS_FLOW[currentStatusIndex + 1] : null;
  const canApproveStory = user?.role !== 'ebi_coordenadora';
  const canMoveForward = !!nextStatus && (nextStatus !== 'aprovada' || canApproveStory);

  const updateForm = (patch) => setForm(prev => ({ ...prev, ...patch }));

  const requestStatusChange = (targetStatus, direction) => {
    if (!targetStatus || targetStatus === form.status) return;
    setStatusChangeTarget({ from: form.status || 'recebida', to: targetStatus, direction });
  };

  const statusNotificationInfo = (targetStatus) => {
    const key = targetStatus === 'corrigida' ? 'corrected' : targetStatus === 'aprovada' ? 'approved' : '';
    const config = ebiSettings?.whatsapp_notifications || {};
    const recipients = key ? (config[key]?.recipients || []) : [];
    const active = !!(ebiSettings?.whatsapp_connection?.connected && config.enabled && key && config[key]?.enabled && recipients.length);
    return { active, recipients };
  };

  const getAiCorrection = (field) => {
    const direct = aiCorrections?.[field];
    if (direct && String(direct).trim()) return String(direct).trim();
    const item = aiItems.find(entry => entry.campo_corrigivel === field && entry.sugestao && entry.parecer !== 'APROVADO');
    return item?.sugestao ? String(item.sugestao).trim() : '';
  };

  const openFieldCorrection = (field) => {
    const suggestion = getAiCorrection(field);
    if (!suggestion) return;
    setFieldCorrectionTarget({ field, suggestion, currentValue: form[field] || '' });
  };

  const applyFieldCorrection = async () => {
    if (!fieldCorrectionTarget?.field) return;
    const meta = CORRECTION_FIELD_META[fieldCorrectionTarget.field] || {};
    const value = meta.html ? suggestionToHtml(fieldCorrectionTarget.suggestion) : fieldCorrectionTarget.suggestion;
    const nextForm = { ...form, [fieldCorrectionTarget.field]: value };
    setForm(nextForm);
    setFieldCorrectionTarget(null);
    await persistStory({ silent: false, payload: nextForm });
  };

  const persistStory = useCallback(async ({ silent = false, payload = null } = {}) => {
    const storyPayload = payload || form;
    if (!storyId || !storyPayload?.story_id) return null;
    if (!silent) setSaving(true);
    if (silent) setAutoSaveState('saving');
    try {
      const r = await fetch(`${API}/ebi/stories/${storyId}`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(storyPayload) });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Erro ao salvar história');
      setStory(data);
      setForm(data);
      lastSavedSnapshotRef.current = JSON.stringify(data);
      if (silent) setAutoSaveState('saved');
      if (!silent) toast.success('História atualizada');
      return data;
    } catch (err) {
      if (silent) setAutoSaveState('error');
      toast.error(err.message || 'Erro ao salvar');
      return null;
    } finally {
      if (!silent) setSaving(false);
    }
  }, [form, storyId]);

  const saveOnBlur = useCallback(async (patch = null) => {
    if (initialLoadRef.current || !form?.story_id || saving) return;
    const nextForm = patch ? { ...form, ...patch } : form;
    if (patch) setForm(nextForm);
    const snapshot = JSON.stringify(nextForm);
    if (snapshot === lastSavedSnapshotRef.current) return;
    setAutoSaveState('saving');
    await persistStory({ silent: true, payload: nextForm });
  }, [form, persistStory, saving]);

  const saveStory = async () => {
    setSaving(true);
    await persistStory({ silent: false });
    setSaving(false);
  };

  const confirmStatusChange = async () => {
    if (!statusChangeTarget?.to) return;
    const nextForm = { ...form, status: statusChangeTarget.to };
    setForm(nextForm);
    setStatusChangeTarget(null);
    await persistStory({ silent: false, payload: nextForm });
  };

  const printStory = () => {
    window.open(`/ebi/historias/${storyId}/imprimir`, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    if (initialLoadRef.current || !form?.story_id) return;
    const snapshot = JSON.stringify(form);
    if (snapshot !== lastSavedSnapshotRef.current) setAutoSaveState('pending');
  }, [form]);

  const analyzeStory = async () => {
    setAnalyzing(true);
    try {
      const r = await fetch(`${API}/ebi/stories/${storyId}/analyze`, { method: 'POST', credentials: 'include' });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Erro ao analisar história');
      let analysis = data;
      if (data.job_id && data.status && !data.result) {
        toast.info('Análise IA iniciada');
        analysis = await pollEbiAiJob(storyId, data.job_id);
      }
      const nextAnalyses = analysis.cache_hit ? (form.ai_analyses || []) : [...(form.ai_analyses || []), analysis];
      const next = { ...form, last_ai_analysis: analysis, ai_analyses: nextAnalyses };
      setForm(next);
      setStory(next);
      setAiDialogOpen(true);
      toast.success(analysis.cache_hit ? 'Análise IA reaproveitada' : 'Análise IA concluída');
    } catch (err) {
      toast.error(err.message || 'Erro ao analisar');
    } finally {
      setAnalyzing(false);
    }
  };

  const addObservation = () => {
    if (!observationText.trim()) { toast.error('Digite a observação'); return; }
    updateForm({ observations: [...(form.observations || []), { id: `obs_${Date.now()}`, text: observationText.trim(), public: observationPublic, created_at: new Date().toISOString() }] });
    setObservationText('');
    setObservationPublic(false);
    setObservationDialogOpen(false);
  };

  const removeObservation = (obs) => {
    const key = obs.id || obs.created_at || obs.text;
    updateForm({ observations: (form.observations || []).filter(item => (item.id || item.created_at || item.text) !== key) });
  };

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    setSaving(true);
    try {
      for (const file of Array.from(files)) {
        const body = new FormData();
        body.append('file', file);
        const r = await fetch(`${API}/ebi/stories/${storyId}/files`, { method: 'POST', credentials: 'include', body });
        if (!r.ok) throw new Error('Erro ao anexar arquivo');
      }
      toast.success('Arquivo(s) anexado(s)');
      await loadData();
    } catch (err) {
      toast.error(err.message || 'Erro ao anexar');
    } finally {
      setSaving(false);
    }
  };

  const updateAttachment = async (attachment, patch) => {
    try {
      const payload = { uploaded_by_role: patch.uploaded_by_role ?? attachment.uploaded_by_role ?? 'ministerio', public_visible: patch.public_visible ?? !!attachment.public_visible };
      const r = await fetch(`${API}/ebi/stories/${storyId}/files/${attachment.file_id}`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Erro ao atualizar arquivo');
      setStory(data);
      setForm(data);
      toast.success('Arquivo atualizado');
    } catch (err) {
      toast.error(err.message || 'Erro ao atualizar arquivo');
    }
  };

  const deleteAttachment = async (attachment) => {
    try {
      const r = await fetch(`${API}/ebi/stories/${storyId}/files/${attachment.file_id}`, { method: 'DELETE', credentials: 'include' });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Erro ao excluir arquivo');
      updateForm({ attachments: (form.attachments || []).filter(item => item.file_id !== attachment.file_id) });
      setDeleteAttachmentTarget(null);
      toast.success('Arquivo excluído');
    } catch (err) {
      toast.error(err.message || 'Erro ao excluir arquivo');
    }
  };

  const openAttachment = async (event, file) => {
    event.preventDefault();
    if (!file?.file_id) return;
    setDownloadingFileId(file.file_id);
    try {
      const r = await fetch(storyFileUrl(storyId, file), { credentials: 'include' });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.detail || 'Arquivo não encontrado');
      }
      const blob = await r.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      toast.error(err.message || 'Não foi possível abrir o arquivo');
    } finally {
      setDownloadingFileId('');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16" data-testid="ebi-story-detail-loading"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  }

  if (!story) {
    return <div className="flex items-center justify-center py-16" data-testid="ebi-story-detail-not-found"><Card><CardContent className="p-8 text-center"><p className="text-slate-600">História não encontrada.</p><Button className="mt-4" onClick={() => (onBack ? onBack() : navigate('/admin'))} data-testid="ebi-story-detail-not-found-back">Voltar</Button></CardContent></Card></div>;
  }

  return (
    <div className={`${embedded ? '' : 'min-h-screen bg-slate-50'} max-w-full overflow-x-hidden`} data-testid="ebi-story-detail-page">
      <main className={`${embedded ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'} max-w-full overflow-x-hidden`}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6" data-testid="ebi-story-detail-reuniao-header">
          <Button variant="outline" size="sm" onClick={() => (onBack ? onBack() : navigate('/admin', { state: { activeSection: 'ebi-historias' } }))} className="bg-white w-fit" data-testid="ebi-story-detail-back-button" title="Voltar">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-semibold text-slate-900 truncate" style={{ fontFamily: 'Work Sans, sans-serif' }} data-testid="ebi-story-detail-title">{form.title || 'História EBI'}</h2>
              <StatusBadge status={form.status} />
            </div>
            <div className="flex items-center gap-3 text-sm text-slate-400 mt-1 flex-wrap">
              <span className="flex items-center gap-1" data-testid="ebi-story-detail-group"><UsersRound className="w-3.5 h-3.5" />{form.group_name || 'Sem grupo'}</span>
              <span className="flex items-center gap-1" data-testid="ebi-story-detail-date"><CalendarDays className="w-3.5 h-3.5" />{fmtDate(form.telling_date) || 'Sem data'}</span>
              <span className="flex items-center gap-1" data-testid="ebi-story-detail-email"><Mail className="w-3.5 h-3.5" />{form.tracking_email || 'Sem e-mail'}</span>
              <span className="flex items-center gap-1" data-testid="ebi-story-detail-whatsapp"><Smartphone className="w-3.5 h-3.5" />{formatPhone(form.submitter_whatsapp || '') || 'Sem WhatsApp'}</span>
            </div>
          </div>
          <div className="flex gap-1 items-center flex-wrap">
            <TooltipProvider delayDuration={200}>
              {aiAvailable && lastAnalysis?.result && (
                <UiTooltip><TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" onClick={() => setAiDialogOpen(true)} className={aiApproved === (aiItems.length || 15) ? 'text-emerald-600 hover:bg-emerald-50' : 'text-amber-600 hover:bg-amber-50'} data-testid="ebi-story-detail-ai-view-button">
                    <Eye className="w-4 h-4" />
                  </Button>
                </TooltipTrigger><TooltipContent>Ver análise IA ({aiApproved}/{aiItems.length || 15})</TooltipContent></UiTooltip>
              )}
              {aiAvailable && <UiTooltip><TooltipTrigger asChild>
                <Button variant="ghost" size="sm" onClick={analyzeStory} disabled={analyzing || aiSettings.enabled === false || user?.role === 'ebi_coordenadora'} className="text-blue-600 hover:bg-blue-50" data-testid="ebi-story-detail-ai-analyze-button">
                  {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                </Button>
              </TooltipTrigger><TooltipContent>Analisar com IA</TooltipContent></UiTooltip>}
              <UiTooltip><TooltipTrigger asChild>
                <Button variant="ghost" size="sm" onClick={printStory} className="text-slate-700 hover:bg-slate-100" data-testid="ebi-story-detail-print-button">
                  <Printer className="w-4 h-4" />
                </Button>
              </TooltipTrigger><TooltipContent>Imprimir história</TooltipContent></UiTooltip>
              <UiTooltip><TooltipTrigger asChild>
                <Button variant="ghost" size="sm" onClick={saveStory} disabled={saving} className="text-blue-600 hover:bg-blue-50" data-testid="ebi-story-detail-save-button">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                </Button>
              </TooltipTrigger><TooltipContent>Salvar história</TooltipContent></UiTooltip>
            </TooltipProvider>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 max-w-full overflow-x-hidden" data-testid="ebi-story-detail-reuniao-grid">
          <section className="lg:col-span-3 space-y-4 min-w-0">
            <Card className="border-0 shadow-sm overflow-hidden flex flex-col min-w-0 max-w-full" data-testid="ebi-story-detail-story-card">
              <div className="bg-blue-50 px-4 py-3 border-b border-blue-100 flex items-center justify-between">
                <div className="flex items-center gap-2"><BookOpenText className="w-4 h-4 text-blue-600" /><span className="text-sm font-semibold text-blue-900">História</span></div>
                <Badge className="bg-blue-100 text-blue-600 text-[10px]" data-testid="ebi-story-autosave-status">{autoSaveState === 'saving' ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Salvando...</> : autoSaveState === 'pending' ? <>Pendente</> : autoSaveState === 'error' ? <><X className="w-3 h-3 mr-1" />Erro</> : autoSaveState === 'saved' ? <><CheckCircle2 className="w-3 h-3 mr-1" />Salvo</> : <><CheckCircle2 className="w-3 h-3 mr-1" />Edição</>}</Badge>
              </div>
              <CardContent className="p-0 flex-1 flex flex-col">
                <TipTapEditor content={form.story_html || ''} onChange={html => updateForm({ story_html: html })} onBlur={() => saveOnBlur()} placeholder="Edite a história..." className="ebi-richtext-input" minHeight="420px" enterAsHardBreak testId="ebi-story-richtext-story-editor" />
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm overflow-hidden min-w-0 max-w-full" data-testid="ebi-story-detail-foundation-card">
              <div className="bg-blue-50 px-4 py-3 border-b border-blue-100 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-blue-600" /><span className="text-sm font-semibold text-blue-900">Fundamento</span></div><CorrectionButton field="foundation" suggestion={getAiCorrection('foundation')} onApply={openFieldCorrection} />
              </div>
              <CardContent className="p-0"><TipTapEditor content={form.foundation || ''} onChange={html => updateForm({ foundation: html })} onBlur={() => saveOnBlur()} placeholder="Digite o fundamento da história..." className="ebi-richtext-input" minHeight="220px" enterAsHardBreak testId="ebi-story-richtext-foundation-editor" /></CardContent>
            </Card>

            <Card className="border-0 shadow-sm overflow-hidden min-w-0 max-w-full" data-testid="ebi-story-detail-objective-card">
              <div className="bg-blue-50 px-4 py-3 border-b border-blue-100 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-blue-600" /><span className="text-sm font-semibold text-blue-900">Objetivo</span></div><CorrectionButton field="objective" suggestion={getAiCorrection('objective')} onApply={openFieldCorrection} />
              </div>
              <CardContent className="p-0"><TipTapEditor content={form.objective || ''} onChange={html => updateForm({ objective: html })} onBlur={() => saveOnBlur()} placeholder="Digite o objetivo da história..." className="ebi-richtext-input" minHeight="180px" enterAsHardBreak testId="ebi-story-richtext-objective-editor" /></CardContent>
            </Card>

            <Card className="border-0 shadow-sm overflow-hidden min-w-0 max-w-full" data-testid="ebi-story-detail-activities-card">
              <div className="bg-blue-50 px-4 py-3 border-b border-blue-100 flex items-center justify-between">
                <div className="flex items-center gap-2"><ClipboardList className="w-4 h-4 text-blue-600" /><span className="text-sm font-semibold text-blue-900">Atividades / Lembrancinha</span></div>
              </div>
              <CardContent className="p-0"><TipTapEditor content={form.activities || ''} onChange={html => updateForm({ activities: html })} onBlur={() => saveOnBlur()} placeholder="Digite as atividades e lembrancinha..." className="ebi-richtext-input" minHeight="220px" enterAsHardBreak testId="ebi-story-richtext-activities-editor" /></CardContent>
            </Card>

            <Card className="border-0 shadow-sm overflow-hidden min-w-0 max-w-full" data-testid="ebi-story-detail-glossary-card">
              <div className="bg-blue-50 px-4 py-3 border-b border-blue-100 flex items-center justify-between">
                <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-blue-600" /><span className="text-sm font-semibold text-blue-900">Glossário</span></div>
              </div>
              <CardContent className="p-0"><TipTapEditor content={form.glossary || ''} onChange={html => updateForm({ glossary: html })} onBlur={() => saveOnBlur()} placeholder="Digite o glossário, se necessário..." className="ebi-richtext-input" minHeight="180px" enterAsHardBreak testId="ebi-story-richtext-glossary-editor" /></CardContent>
            </Card>
          </section>

          <aside className="lg:col-span-2 flex flex-col gap-4 min-w-0 max-w-full" data-testid="ebi-story-detail-side-panel">
            <Card className="border-0 shadow-sm overflow-hidden min-w-0 max-w-full" data-testid="ebi-story-detail-data-card">
              <div className="bg-blue-50 px-4 py-3 border-b border-blue-100 flex items-center justify-between">
                <div className="flex items-center gap-2"><UserRound className="w-4 h-4 text-blue-600" /><span className="text-sm font-semibold text-blue-900">Dados</span></div>
              </div>
              <CardContent className="p-3 space-y-3">
                <div className="grid sm:grid-cols-2 gap-3"><div><Label>E-mail</Label><Input type="email" value={form.tracking_email || ''} onChange={e => updateForm({ tracking_email: e.target.value })} onBlur={() => saveOnBlur()} data-testid="ebi-story-detail-field-tracking-email" /></div><div><Label>WhatsApp</Label><Input inputMode="tel" value={formatPhone(form.submitter_whatsapp || '')} onChange={e => updateForm({ submitter_whatsapp: formatPhone(e.target.value) })} onBlur={() => saveOnBlur()} placeholder="(11) 99999-9999" data-testid="ebi-story-detail-field-submitter-whatsapp" /></div></div>
                <div><Label>Data</Label><Input type="date" value={form.telling_date || ''} onChange={e => updateForm({ telling_date: e.target.value })} onBlur={() => saveOnBlur()} data-testid="ebi-story-detail-field-telling-date" /></div>
                <div className="grid sm:grid-cols-2 gap-3"><div><Label>Grupo</Label><Select value={form.group_id || ''} onValueChange={value => { const group = groups.find(g => g.group_id === value); saveOnBlur({ group_id: value, group_name: group?.name || '', official_storyteller: '' }); }}><SelectTrigger data-testid="ebi-story-detail-field-group"><SelectValue placeholder="Selecione" /></SelectTrigger><SelectContent>{groups.length === 0 ? <SelectItem value="__empty" disabled>Nenhum grupo</SelectItem> : groups.map(g => <SelectItem key={g.group_id} value={g.group_id}>{g.name}</SelectItem>)}</SelectContent></Select></div><div><Label>Contadora</Label><Select value={form.official_storyteller || ''} disabled={!form.group_id || storytellersForGroup.length === 0} onValueChange={value => saveOnBlur({ official_storyteller: value })}><SelectTrigger data-testid="ebi-story-detail-field-storyteller"><SelectValue placeholder="Selecione" /></SelectTrigger><SelectContent>{storytellersForGroup.length === 0 ? <SelectItem value="__empty" disabled>Nenhuma disponível</SelectItem> : storytellersForGroup.map(person => <SelectItem key={person.person_id} value={person.name}>{person.name}</SelectItem>)}</SelectContent></Select></div></div>
                <div><div className="mb-1 flex items-center justify-between gap-2"><Label>Título</Label><CorrectionButton field="title" suggestion={getAiCorrection('title')} onApply={openFieldCorrection} /></div><Input value={form.title || ''} onChange={e => updateForm({ title: e.target.value })} onBlur={() => saveOnBlur()} data-testid="ebi-story-detail-field-title" /></div>
                <div><div className="mb-1 flex items-center justify-between gap-2"><Label>Ref. Bíblica</Label><CorrectionButton field="biblical_reference" suggestion={getAiCorrection('biblical_reference')} onApply={openFieldCorrection} /></div><Input value={form.biblical_reference || ''} onChange={e => updateForm({ biblical_reference: e.target.value })} onBlur={() => saveOnBlur()} data-testid="ebi-story-detail-field-biblical-reference" /></div>
                <div className="rounded-lg border border-slate-100 bg-slate-50 p-3" data-testid="ebi-story-detail-status-flow-card">
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <Label className="text-slate-700">Status</Label>
                    <StatusBadge status={form.status} testId="ebi-story-detail-status-flow-current-badge" />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    {previousStatus ? (
                      <Button type="button" variant="outline" size="sm" disabled={saving} onClick={() => requestStatusChange(previousStatus, 'backward')} className="bg-white justify-center" data-testid="ebi-story-detail-status-back-button">
                        <ArrowLeft className="w-4 h-4 mr-1" />Voltar para {STATUS[previousStatus]?.label}
                      </Button>
                    ) : <span aria-hidden="true" />}
                    {canMoveForward && (
                      <Button type="button" size="sm" disabled={saving} onClick={() => requestStatusChange(nextStatus, 'forward')} className="ml-auto bg-blue-600 hover:bg-blue-700 justify-center" data-testid="ebi-story-detail-status-next-button">
                        {nextStatus === 'aprovada' ? 'Aprovar História' : `Avançar para ${STATUS[nextStatus]?.label}`}<ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                  </div>
                  {currentStatusIndex === -1 && <p className="mt-2 text-xs text-slate-500" data-testid="ebi-story-detail-status-flow-legacy-message">Este status não faz parte do fluxo linear atual.</p>}
                  {!canMoveForward && nextStatus === 'aprovada' && <p className="mt-2 text-xs text-slate-500" data-testid="ebi-story-detail-status-flow-role-message">A aprovação final é feita pelo ministério responsável.</p>}
                </div>
              </CardContent>
            </Card>

            {aiAvailable && <Card className="border-0 shadow-sm overflow-hidden min-w-0 max-w-full" data-testid="ebi-story-detail-ai-card">
              <div className="bg-blue-50 px-4 py-3 border-b border-blue-100 flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0"><Sparkles className="w-4 h-4 text-blue-600 flex-shrink-0" /><span className="text-sm font-semibold text-blue-900">Análise IA</span>{lastAnalysis?.result && <Badge className={`${statusClass(lastAnalysis.result.parecer_geral)} text-xs max-w-[160px] truncate`} data-testid="ebi-story-detail-ai-header-status">{aiVerdictLabel(lastAnalysis.result.parecer_geral)}</Badge>}{lastAnalysis?.cache_hit && <Badge variant="outline" className="bg-white text-[10px] font-medium text-slate-500 border-slate-200" data-testid="ebi-story-detail-ai-cache-indicator">reaproveitada</Badge>}</div>
                <span className="text-xs text-blue-600 flex-shrink-0">{lastAnalysis?.result ? `${aiApproved}/${aiItems.length || 15}` : 'sem análise'}</span>
              </div>
              <CardContent className="p-3 space-y-3">{lastAnalysis?.result ? <button type="button" onClick={() => setAiDialogOpen(true)} className="w-full text-left" data-testid="ebi-story-detail-ai-summary-open">{lastAnalysis.result.resumo ? <p className="text-xs text-slate-600 line-clamp-3 hover:text-blue-700">{lastAnalysis.result.resumo}</p> : <p className="text-sm text-slate-500 hover:text-blue-700">Abrir análise</p>}</button> : <p className="text-sm text-slate-500">Nenhuma análise salva.</p>}</CardContent>
            </Card>}

            <Card className="border-0 shadow-sm overflow-hidden min-w-0 max-w-full" data-testid="ebi-story-detail-files-card">
              <div className="bg-blue-50 px-4 py-3 border-b border-blue-100 flex items-center justify-between">
                <div className="flex items-center gap-2"><UploadCloud className="w-4 h-4 text-blue-600" /><span className="text-sm font-semibold text-blue-900">Arquivos</span></div>
                <label className="relative inline-flex h-7 w-7 items-center justify-center overflow-hidden rounded-md border border-blue-200 bg-white hover:bg-blue-50 cursor-pointer" data-testid="ebi-story-detail-file-upload-label" title="Anexar arquivo"><Plus className="w-4 h-4 text-blue-600" /><input type="file" multiple onChange={e => uploadFiles(e.target.files)} className="absolute inset-0 h-full w-full cursor-pointer opacity-0" data-testid="ebi-story-detail-file-upload" /></label>
              </div>
              <CardContent className="p-3">{(form.attachments || []).length === 0 ? <p className="text-sm text-slate-500">Nenhum arquivo anexado.</p> : <div className="space-y-2" data-testid="ebi-story-detail-file-list">{(form.attachments || []).map(file => <div key={file.file_id} className="rounded-lg bg-slate-50 p-2" data-testid={`ebi-story-detail-file-${file.file_id}`}><div className="flex items-center gap-2"><a href={storyFileUrl(storyId, file)} onClick={(event) => openAttachment(event, file)} className="flex-1 min-w-0 text-sm text-slate-700 hover:text-blue-700 flex items-center gap-1.5" data-testid={`ebi-story-detail-file-download-${file.file_id}`}><Download className="w-3.5 h-3.5 flex-shrink-0" />{downloadingFileId === file.file_id && <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" data-testid={`ebi-story-detail-file-download-loading-${file.file_id}`} />}<span className="truncate">{file.name}</span></a><Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:bg-red-50" onClick={() => setDeleteAttachmentTarget(file)} data-testid={`ebi-story-detail-file-delete-${file.file_id}`}><Trash2 className="w-3.5 h-3.5" /></Button></div><div className="mt-2 flex flex-wrap items-center gap-2"><Select value={file.uploaded_by_role || 'ministerio'} onValueChange={value => updateAttachment(file, { uploaded_by_role: value })}><SelectTrigger className="h-8 flex-1 min-w-[130px]" data-testid={`ebi-story-detail-file-role-${file.file_id}`}><SelectValue /></SelectTrigger><SelectContent>{Object.entries(ATTACHMENT_ROLE_LABEL).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent></Select><label className="inline-flex h-8 items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 text-xs text-slate-600" data-testid={`ebi-story-detail-file-public-wrap-${file.file_id}`}><Switch checked={!!file.public_visible} onCheckedChange={value => updateAttachment(file, { public_visible: value })} data-testid={`ebi-story-detail-file-public-${file.file_id}`} /><span>Público</span></label></div></div>)}</div>}</CardContent>
            </Card>

            <Card className="border-0 shadow-sm overflow-hidden min-w-0 max-w-full" data-testid="ebi-story-detail-notes-card">
              <div className="bg-blue-50 px-4 py-3 border-b border-blue-100 flex items-center justify-between">
                <div className="flex items-center gap-2"><MessageSquare className="w-4 h-4 text-blue-600" /><span className="text-sm font-semibold text-blue-900">Observações</span></div>
                <Button variant="outline" size="icon" className="h-7 w-7 bg-white border-blue-200 hover:bg-blue-50" onClick={() => setObservationDialogOpen(true)} data-testid="ebi-story-detail-observation-open-modal" title="Adicionar observação"><Plus className="w-4 h-4 text-blue-600" /></Button>
              </div>
              <CardContent className="p-3"><div className="space-y-2" data-testid="ebi-story-detail-observation-list">{(form.observations || []).length === 0 ? <p className="text-sm text-slate-500">Nenhuma observação adicionada.</p> : (form.observations || []).map(obs => <div key={obs.id || obs.created_at || obs.text} className="rounded-lg bg-slate-50 p-2"><div className="flex items-start justify-between gap-2"><div className="min-w-0"><Badge className={obs.public ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}>{obs.public ? 'Pública' : 'Interna'}</Badge><p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap break-words">{obs.text}</p></div><Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:bg-red-50 flex-shrink-0" onClick={() => setDeleteObservationTarget(obs)} data-testid={`ebi-story-detail-observation-remove-${obs.id || obs.created_at || 'item'}`}><Trash2 className="w-3.5 h-3.5" /></Button></div></div>)}</div></CardContent>
            </Card>

            <Card className="border-0 shadow-sm overflow-hidden min-w-0 max-w-full" data-testid="ebi-story-detail-timeline-card">
              <div className="bg-blue-50 px-4 py-3 border-b border-blue-100 flex items-center justify-between"><div className="flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-blue-600" /><span className="text-sm font-semibold text-blue-900">Timeline</span></div></div>
              <CardContent className="p-3">{(form.timeline || []).length === 0 ? <p className="text-sm text-slate-500">Sem histórico registrado.</p> : <div className="relative" data-testid="ebi-story-detail-timeline">{(form.timeline || []).slice().reverse().slice(0, 8).map((item, index, list) => <div key={`${item.at}-${index}`} className="relative flex gap-2.5 pb-3 last:pb-0 text-xs" data-testid={`ebi-story-detail-timeline-item-${index}`}>{index < list.length - 1 && <span className="absolute left-[5px] top-3 bottom-0 w-px bg-blue-100" aria-hidden="true" />}<span className="relative z-10 mt-1 w-2.5 h-2.5 rounded-full bg-blue-500 ring-4 ring-blue-50 flex-shrink-0" /><span className="min-w-0 text-slate-600 leading-snug"><strong className="text-slate-800">{STATUS[item.status]?.label || item.status || item.action}</strong>{item.by ? ` por ${item.by}` : ''}<br />{item.at ? formatBrazilDateTime(item.at, item.at) : ''}</span></div>)}</div>}</CardContent>
            </Card>
          </aside>
        </div>
      </main>

      {aiAvailable && <AiAnalysisDialog analysis={lastAnalysis} open={aiDialogOpen} onOpenChange={setAiDialogOpen} />}
      <Dialog open={observationDialogOpen} onOpenChange={setObservationDialogOpen}>
        <DialogContent className="max-w-md" data-testid="ebi-story-detail-observation-dialog">
          <DialogHeader><DialogTitle>Adicionar observação</DialogTitle><DialogDescription>Registre uma observação interna ou pública para o acompanhamento.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Textarea value={observationText} onChange={e => setObservationText(e.target.value)} placeholder="Nova observação..." data-testid="ebi-story-detail-observation-text" />
            <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={observationPublic} onChange={e => setObservationPublic(e.target.checked)} data-testid="ebi-story-detail-observation-public" /> Pública</label>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setObservationDialogOpen(false)} data-testid="ebi-story-detail-observation-cancel">Cancelar</Button><Button onClick={addObservation} className="bg-blue-600 hover:bg-blue-700" data-testid="ebi-story-detail-observation-add"><Plus className="w-4 h-4 mr-1" />Adicionar</Button></div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!deleteObservationTarget} onOpenChange={(open) => !open && setDeleteObservationTarget(null)}>
        <DialogContent className="max-w-md" data-testid="ebi-story-detail-delete-observation-dialog">
          <DialogHeader><DialogTitle>Excluir observação?</DialogTitle><DialogDescription>Essa observação será removida desta história.</DialogDescription></DialogHeader>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setDeleteObservationTarget(null)} data-testid="ebi-story-detail-delete-observation-cancel">Cancelar</Button><Button variant="destructive" onClick={() => { removeObservation(deleteObservationTarget); setDeleteObservationTarget(null); }} data-testid="ebi-story-detail-delete-observation-confirm">Excluir</Button></div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!deleteAttachmentTarget} onOpenChange={(open) => !open && setDeleteAttachmentTarget(null)}>
        <DialogContent className="max-w-md" data-testid="ebi-story-detail-delete-file-dialog">
          <DialogHeader><DialogTitle>Excluir arquivo?</DialogTitle><DialogDescription>O arquivo será removido desta história.</DialogDescription></DialogHeader>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setDeleteAttachmentTarget(null)} data-testid="ebi-story-detail-delete-file-cancel">Cancelar</Button><Button variant="destructive" onClick={() => deleteAttachment(deleteAttachmentTarget)} data-testid="ebi-story-detail-delete-file-confirm">Excluir</Button></div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!statusChangeTarget} onOpenChange={(open) => !open && setStatusChangeTarget(null)}>
        <DialogContent className="max-w-md" data-testid="ebi-story-detail-status-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Alterar status da história?</DialogTitle>
            <DialogDescription data-testid="ebi-story-detail-status-confirm-description">
              Realmente deseja alterar o status de {STATUS[statusChangeTarget?.from]?.label || 'Recebida'} para {STATUS[statusChangeTarget?.to]?.label || 'Corrigida'}?
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm text-slate-600" data-testid="ebi-story-detail-status-confirm-flow">
            Fluxo obrigatório: Recebida → Corrigida → Aprovada.
          </div>
          {statusNotificationInfo(statusChangeTarget?.to).active && <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-sm text-emerald-700" data-testid="ebi-story-detail-status-whatsapp-notice">Também será enviada uma mensagem WhatsApp para {statusNotificationInfo(statusChangeTarget?.to).recipients.length} número(s): {statusNotificationInfo(statusChangeTarget?.to).recipients.join(', ')}.</div>}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setStatusChangeTarget(null)} data-testid="ebi-story-detail-status-confirm-cancel">Cancelar</Button>
            <Button onClick={confirmStatusChange} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="ebi-story-detail-status-confirm-submit">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}Confirmar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!fieldCorrectionTarget} onOpenChange={(open) => !open && setFieldCorrectionTarget(null)}>
        <DialogContent className="max-w-lg" data-testid="ebi-story-detail-ai-correction-dialog">
          <DialogHeader>
            <DialogTitle>Aplicar correção da IA?</DialogTitle>
            <DialogDescription data-testid="ebi-story-detail-ai-correction-description">
              A IA sugeriu substituir o campo {CORRECTION_FIELD_META[fieldCorrectionTarget?.field]?.label || 'selecionado'}. Confira antes de aplicar.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-slate-100 bg-slate-50 p-3" data-testid="ebi-story-detail-ai-correction-current">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Valor atual</p>
              <p className="max-h-28 overflow-y-auto whitespace-pre-wrap text-sm text-slate-600">{stripHtml(fieldCorrectionTarget?.currentValue || '') || 'Vazio'}</p>
            </div>
            <div className="rounded-lg border border-blue-100 bg-blue-50 p-3" data-testid="ebi-story-detail-ai-correction-suggestion">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-blue-500">Sugestão da IA</p>
              <p className="max-h-40 overflow-y-auto whitespace-pre-wrap text-sm text-blue-900">{fieldCorrectionTarget?.suggestion || ''}</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setFieldCorrectionTarget(null)} data-testid="ebi-story-detail-ai-correction-cancel">Cancelar</Button>
              <Button onClick={applyFieldCorrection} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="ebi-story-detail-ai-correction-confirm">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}Aplicar correção
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function EbiStoryDetailPage() {
  return <EbiStoryDetailContent />;
}